/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Inject, NgModule } from '@angular/core';
import { ExtensionService, provideExtensionConfig } from '@alfresco/adf-extensions';
import { provideEffects } from '@ngrx/effects';
import { ConvertToPdfEffects } from '../../effects/convert-pdf-effects';
import { MailToEffects } from '../../effects/mail-to-effects';
import {
  canAddComment,
  canConvertToPDF,
  canEditText,
  canInitiateProcess,
  canRename,
  canSendMailTo,
  isAdministrator,
  isShared
} from '../../alg-extensions.rules';
import { supportedExtensions } from '@alfresco/aca-shared/rules';
import { RenameEffects } from '../../effects/rename-effects';
import { RenameDialogComponent } from './dialogs/rename-dialog/rename-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, DatePipe, DOCUMENT, NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { AddNoteEffects } from '../../effects/add-note-effects';
import { DeleteSharedLinkEffects } from '../../effects/delete-shared-link-effects';
import { MailToSharedEffects } from '../../effects/mail-to-shared-effects';
import { EditTextEffects } from '../../effects/edit-text.effects';
import { EditTextDialogComponent } from './dialogs/edit-text/edit-text-dialog.component';
import { DialogEffects } from '../../effects/dialog.effects';
import { MyTasksComponent } from './components/my-tasks/my-tasks.component';
import { MyProcessesComponent } from './components/my-processes/my-processes.component';
import { ProcessDetailComponent } from './components/process-detail/process-detail.component';
import { FormComponent } from './components/form/form.component';
import { FormProcessusDialogComponent } from './components/form-processus-dialog/form-processus-dialog.component';
import { FormTaskDialogComponent } from './components/form-task-dialog/form-task-dialog';
import { ViewProcessComponent } from './components/view-process/view-process.component';
import { SelectFilesComponent } from './components/select-files/select-files.component';
import { TraductionPipe } from './pipes/traduction.pipe';
import { AppConfigService, DataTableModule, SearchTextModule, TemplateModule, ToolbarModule } from '@alfresco/adf-core';
import { BreadcrumbComponent, DocumentListModule } from '@alfresco/adf-content-services';
import { BrowserModule } from '@angular/platform-browser';
import { InitiateProcessEffects } from '../../effects/initiate-process-effects';
import { MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { getFrenchPaginatorIntl } from './paginator-intl';
import { TranslateModule } from '@ngx-translate/core';
import { AddCommentEffects } from '../../effects/add-comment-effects';
import { provideAlgAcaOverrides } from './alg-aca-overrides';
import { AlgMaterialModule } from './alg-material.module';

/**
 * Extensions ajoutees a l'action « Modifier dans Office ».
 *
 * La valeur designe le PROGRAMME a lancer : elle alimente le schema d'URL `ms-excel:ofe|u|...` que
 * `AosEditOnlineService` construit, autant que la regle de visibilite `aos.canOpenWithOffice`. Les
 * deux lisent le meme objet.
 *
 * CE QUI MANQUAIT. La carte d'ACA couvre Word, Excel, PowerPoint et Visio, y compris leurs modeles
 * et leurs variantes a macros — mais ni le csv, ni aucun format LibreOffice. Or Office ouvre le
 * premier depuis toujours, et l'ODF depuis Office 2013.
 *
 * CE QUI N'EST PAS AJOUTE, ET POURQUOI. Les formats ODF plats (`fodt`, `fods`, `fodp`), le dessin
 * `odg`, le `dbf`, le `dif`, le `slk` et le `txt` sont editables par Collabora mais Office ne sait
 * pas les ouvrir — les annoncer ne produirait qu'un bouton qui echoue. Les deux listes n'ont donc
 * aucune raison d'etre identiques : Collabora edite 29 formats, Office 40.
 */
const OFFICE_EXTRA_EXTENSIONS: Record<string, string> = {
  csv: 'ms-excel',
  odt: 'ms-word',
  ods: 'ms-excel',
  odp: 'ms-powerpoint'
};

@NgModule({
  declarations: [
    RenameDialogComponent,
    EditTextDialogComponent,
    MyTasksComponent,
    MyProcessesComponent,
    ProcessDetailComponent,
    FormComponent,
    FormProcessusDialogComponent,
    FormTaskDialogComponent,
    ViewProcessComponent,
    SelectFilesComponent,
    TraductionPipe
  ],
  imports: [
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    NgIf,
    MatIconModule,
    DatePipe,
    ToolbarModule,
    SearchTextModule,
    BreadcrumbComponent,
    DocumentListModule,
    DataTableModule,
    AsyncPipe,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    AlgMaterialModule,
    MatPaginatorModule,
    TemplateModule,
    TranslateModule
  ],
  providers: [
    // ACA 7.5.0 amorce en standalone : les effets se fournissent via provideEffects,
    // EffectsModule.forFeature() exigeait un EffectsRootModule qui n'existe plus.
    provideEffects([
      ConvertToPdfEffects,
      MailToEffects,
      RenameEffects,
      AddNoteEffects,
      AddCommentEffects,
      EditTextEffects,
      MailToSharedEffects,
      DeleteSharedLinkEffects,
      DialogEffects,
      InitiateProcessEffects
    ]),
    provideExtensionConfig(['alg-extensions.json']),
    { provide: MatPaginatorIntl, useValue: getFrenchPaginatorIntl() },
    provideAlgAcaOverrides()
  ]
})
export class AlgExtensionsModule {
  constructor(extensions: ExtensionService, appConfig: AppConfigService, @Inject(DOCUMENT) document: Document) {
    const applyDocumentLocale = () => {
      const configuredLocale = appConfig.get<string>('locale', appConfig.get<string>('dateValues.defaultLocale', 'en'));
      const locale = configuredLocale?.trim().replace('_', '-').split('.')[0];

      // La langue du document doit suivre la configuration de l'application. Sans cela, le navigateur
      // considere toute l'interface comme anglaise et peut proposer ou appliquer une traduction automatique.
      if (locale && !locale.includes('${')) {
        document.documentElement.lang = locale;
      }
    };

    // Le module d'extension peut etre construit avant la fin du chargement d'app.config.json.
    // `onLoad` est rejoue si la configuration est deja disponible et couvre aussi ses rechargements.
    appConfig.onLoad.subscribe(applyDocumentLocale);

    // On ETEND la carte d'ACA au lieu de la remplacer, et sans toucher a son fichier : ce depot
    // s'interdit de modifier le code communautaire, sous peine de conflit a chaque remontee de
    // version. `supportedExtensions` est exporte par `@alfresco/aca-shared/rules` et lu a chaque
    // evaluation, donc l'etendre ici suffit — la regle de visibilite comme la construction de
    // l'URL de protocole en tiennent compte aussitot.
    Object.assign(supportedExtensions, OFFICE_EXTRA_EXTENSIONS);

    extensions.setEvaluators({
      'alg-extensions.canConvertToPDF': (context) => canConvertToPDF(context),
      'alg-extensions.canAddComment': (context) => canAddComment(context),
      'alg-extensions.canSendMailTo': (context) => canSendMailTo(context),
      'alg-extensions.needSeparator': (context) => canConvertToPDF(context) || canSendMailTo(context),
      'alg-extensions.isShared': (context) => isShared(context),
      'alg-extensions.canEditTxt': (context) => canEditText(context),
      'alg-extensions.canRename': (context) => canRename(context),
      'alg-extensions.canInitiateProcess': (context) => canInitiateProcess(context),
      'alg-extensions.isAdministrator': (context) => isAdministrator(context)
    });
    extensions.setComponents({
      'alg.workflows.components.my-tasks': MyTasksComponent,
      'alg.workflows.components.my-processes': MyProcessesComponent,
      'alg.workflows.components.process-detail': ProcessDetailComponent
    });
  }
}
